/**
 * Probe context for the PDP related-product cards.
 *
 * Sample-intent signals: the buy box has a 2ml Probe selected, or the cart
 * contains only Probe lines. While a signal is active, the bs-product-cards
 * inside <product-recommendations> flip into Probe mode — "2ml Probe" badge,
 * 2ml price, 2ml quick-add, ?variant link — and flip back once the signal is
 * gone. Cards opt in via the data-probe-* attributes rendered by
 * snippets/bs-product-card.liquid; cards whose Probe is sold out stay flakon.
 */
import { ThemeEvents } from '@theme/events';

const PROBE_RE = /2\s?ml/i;

let variantIsProbe = false;
let cartOnlyProbes = false;

/**
 * @param {{ title?: string, public_title?: string } | null | undefined} variant
 * @returns {boolean}
 */
function isProbeVariant(variant) {
  if (!variant) return false;
  return PROBE_RE.test(`${variant.title || ''} ${variant.public_title || ''}`);
}

/**
 * Mirrors the cart-probe-ladder rule: a line is a Probe when an option value
 * normalizes to "2ml"; the variant_title test is a fallback for safety.
 * @param {{ variant_title?: string, options_with_values?: Array<{ value?: string }> }} item
 * @returns {boolean}
 */
function isProbeLine(item) {
  const options = item.options_with_values || [];
  if (options.some((option) => String(option.value || '').toLowerCase().replace(/\s/g, '') === '2ml')) {
    return true;
  }
  return PROBE_RE.test(item.variant_title || '');
}

/** @param {HTMLElement} card */
function flipCard(card) {
  if (card.dataset.probeApplied === 'true') return;

  const price = card.querySelector('.card__price');
  const input = /** @type {HTMLInputElement | null} */ (card.querySelector('input[name="id"]'));
  const link = card.querySelector('.card__link');
  const button = /** @type {HTMLButtonElement | null} */ (card.querySelector('.card__add'));
  if (!price || !input || !link || !button) return;

  card.dataset.origPrice = price.innerHTML;
  card.dataset.origVariantId = input.value;
  card.dataset.origHref = link.getAttribute('href') || '';
  card.dataset.origButtonDisabled = String(button.disabled);
  card.dataset.origButtonLabel = button.textContent?.trim() || '';

  const ownBadge = card.querySelector('.card__badge:not([data-probe-badge])');
  if (ownBadge instanceof HTMLElement) ownBadge.style.display = 'none';
  if (!card.querySelector('[data-probe-badge]')) {
    const badge = document.createElement('span');
    badge.className = 'card__badge';
    badge.setAttribute('data-probe-badge', '');
    badge.textContent = 'Probe';
    const media = card.querySelector('.card__media');
    if (media) {
      media.insertAdjacentElement('beforebegin', badge);
    } else {
      card.prepend(badge);
    }
  }

  price.textContent = card.dataset.probePrice || '';
  input.value = card.dataset.probeVariantId || '';
  link.setAttribute('href', `${card.dataset.origHref.split('?')[0]}?variant=${card.dataset.probeVariantId}`);
  button.disabled = false;
  button.textContent = '+ Warenkorb';
  card.dataset.probeApplied = 'true';
}

/** @param {HTMLElement} card */
function unflipCard(card) {
  if (card.dataset.probeApplied !== 'true') return;

  const price = card.querySelector('.card__price');
  const input = /** @type {HTMLInputElement | null} */ (card.querySelector('input[name="id"]'));
  const link = card.querySelector('.card__link');
  const button = /** @type {HTMLButtonElement | null} */ (card.querySelector('.card__add'));

  if (price && card.dataset.origPrice != null) price.innerHTML = card.dataset.origPrice;
  if (input && card.dataset.origVariantId != null) input.value = card.dataset.origVariantId;
  if (link && card.dataset.origHref != null) link.setAttribute('href', card.dataset.origHref);
  if (button) {
    button.disabled = card.dataset.origButtonDisabled === 'true';
    if (card.dataset.origButtonLabel) button.textContent = card.dataset.origButtonLabel;
  }

  card.querySelector('[data-probe-badge]')?.remove();
  const ownBadge = card.querySelector('.card__badge');
  if (ownBadge instanceof HTMLElement) ownBadge.style.display = '';

  delete card.dataset.probeApplied;
}

function apply() {
  const probeMode = variantIsProbe || cartOnlyProbes;
  for (const grid of document.querySelectorAll('product-recommendations')) {
    if (!(grid instanceof HTMLElement)) continue;
    const maxVisible = parseInt(grid.dataset.maxVisible || '', 10) || Infinity;
    let shown = 0;
    for (const card of grid.querySelectorAll('article.card')) {
      if (!(card instanceof HTMLElement)) continue;
      const flippable = Boolean(card.dataset.probeVariantId) && card.dataset.probeAvailable === 'true';
      if (probeMode && flippable) {
        flipCard(card);
      } else {
        unflipCard(card);
      }
      // Probe mode is a probes-only surface: only cards with a buyable Probe
      // count toward the row; the overflow buffer backfills the hidden ones.
      const eligible = probeMode ? flippable : true;
      const visible = eligible && shown < maxVisible;
      if (visible) shown += 1;
      card.style.display = visible ? '' : 'none';
    }
  }
}

function readInitialVariant() {
  const json =
    document.querySelector('variant-picker[data-template-product-match] script[type="application/json"]') ||
    document.querySelector('variant-picker script[type="application/json"]');
  if (!json) return;
  try {
    variantIsProbe = isProbeVariant(JSON.parse(json.textContent || 'null'));
  } catch {
    variantIsProbe = false;
  }
}

async function readCart() {
  try {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
    if (!response.ok) return;
    const cart = await response.json();
    cartOnlyProbes = cart.item_count > 0 && cart.items.every(isProbeLine);
    apply();
  } catch {
    // Keep the last known state; the next cart:update tries again.
  }
}

document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const picker = event.target;
  if (picker instanceof Element && !picker.hasAttribute('data-template-product-match')) return;
  variantIsProbe = isProbeVariant(/** @type {any} */ (event).detail?.resource);
  apply();
});

document.addEventListener(ThemeEvents.cartUpdate, () => {
  readCart();
});

// The recommendations grid arrives (and gets replaced) via innerHTML swaps —
// re-apply whenever its children change.
for (const recommendations of document.querySelectorAll('product-recommendations')) {
  new MutationObserver(() => apply()).observe(recommendations, { childList: true });
}

readInitialVariant();
apply();
readCart();
